import { uuidv7 } from 'uuidv7';
import $auth from 'app/stores/$auth';
import Model from 'core/model/Model';
import api from 'core/util/api';
import { Socket } from 'core/util/index';
import { computed } from 'mobx';

class ChatSessionModel extends Model {
  socket;

  get urlRoot() {
    return '/api/ui/kaizen/chatSession';
  }

  get defaults() {
    return {
      session_title: 'AI Advisor',
      user_id: $auth.getActiveUserProperty('id'),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      messages: []
    };
  }

  @computed
  get isUserChat() {
    return this.get('user_id') === $auth.getActiveUserProperty('id');
  }

  @computed
  get chatSessionTitle() {
    return this.get('user_title') || this.get('session_title') || 'AI Advisor';
  }

  getUuid() {
    return uuidv7();
  }

  startChatSessionAndAddPrompt(promptText, additionalContext) {
    const new_message_id = this.getUuid();
    const session_id = this.get('id');
    const new_session_id = session_id || this.getUuid();

    if (!session_id) {
      this.set('id', new_session_id);
    }

    // Create optimistic user message (context will be handled as system message on backend)
    const optimisticMessage = {
      id: new_message_id,
      session_id: this.get('id'),
      sender_type: 'USER',
      message_text: promptText,
      created_at: new Date().toISOString(),
      agentTurn: [],
      user: $auth.activeUser
    };

    this.set('messages', [optimisticMessage]);

    this.selectAndSubscribe();
    return api.post(`${this.urlRoot}`, {
      body: {
        new_session_id,
        new_message_id,
        promptText,
        additionalContext
      }
    });
  }

  addPrompt(promptText, additionalContext) {
    const new_message_id = this.getUuid();
    // Optimistically add user prompt to messages
    const currentMessages = this.get('messages') || [];
    const optimisticMessage = {
      id: new_message_id,
      session_id: this.get('id'),
      sender_type: 'USER',
      message_text: promptText,
      created_at: new Date().toISOString(),
      user: $auth.activeUser
    };
    this.set('messages', [...currentMessages, optimisticMessage]);

    return api
      .put(`${this.urlRoot}`, {
        body: {
          session_id: this.get('id'),
          new_message_id,
          promptText,
          additionalContext
        }
      })
      .then(() => {
        // Optionally update the message with server-confirmed data if necessary,
        // though events should handle most updates.
      })
      .catch((err) => {
        console.error('Error in addPrompt:', err);
        // Revert optimistic update or show error
        this.set('messages', currentMessages);
      });
  }

  selectAndSubscribe(options) {
    this.select();
    this.subscribe();
    return this.select(options).then((data) => {
      // Ensure messages are initialized from fetched data if not already present
      if (data && data.messages && (!this.get('messages') || this.get('messages').length === 0)) {
        this.set('messages', data.messages);
      }
      return data;
    });
  }

  onReceiveEvent = (event) => {
    switch (event.event_type) {
      case 'chat_message_created':
        this._handleChatMessageCreated(event);
        break;
      case 'chat_session_updated':
        this._handleChatSessionUpdated(event);
        break;
      case 'agent_turn_started':
        this._handleAgentTurnStarted(event);
        break;
      case 'agent_initialized':
        // No direct modification to messages structure for this event currently
        break;
      case 'agent_step_started':
        this._handleAgentStepStarted(event);
        break;
      case 'agent_step_updated':
        this._handleAgentStepUpdated(event);
        break;
      case 'tool_call_started':
        this._handleToolCallStarted(event);
        break;
      case 'tool_call_succeeded':
        this._handleToolCallSucceeded(event);
        break;
      case 'tool_call_failed':
        this._handleToolCallFailed(event);
        break;
      case 'agent_step_completed':
        this._handleAgentStepCompleted(event);
        break;
      case 'agent_turn_completed':
        this._handleAgentTurnCompleted(event);
        break;
      case 'agent_turn_failed':
        this._handleAgentTurnFailed(event);
        break;
      default:
        console.warn('Unknown event_type:', event.event_type);
    }
  };

  _handleChatMessageCreated(event) {
    const messages = this.get('messages') || [];
    // Avoid duplicating the message if it was optimistically added
    if (!messages.find((msg) => msg.id === event.data.id)) {
      this.set('messages', [...messages, { ...event.data, agentTurn: event.data.agentTurn || [] }]);
    } else {
      // If message exists (e.g. optimistic update), update it with server data
      this.set(
        'messages',
        messages.map((msg) =>
          msg.id === event.data.id
            ? { ...msg, ...event.data, agentTurn: msg.agentTurn || event.data.agentTurn || [] }
            : msg
        )
      );
    }
  }

  _handleChatSessionUpdated(event) {
    const title = event?.data?.session_title;
    if (title) {
      this.set('session_title', title);
    }
  }

  _handleOnAgentStep(event) {
    // Handles event listener from Model consumer, i.e. a React component.
    if (typeof this.onAgentStep === 'function') {
      this.onAgentStep(event);
    }
  }

  _handleAgentTurnStarted(event) {
    const messages = this.get('messages') || [];
    const updatedMessages = messages.map((msg) => {
      if (msg.id === event.user_message_id) {
        const agentTurns = msg.agentTurn || [];
        // Avoid duplicating the agent turn
        if (!agentTurns.find((turn) => turn.id === event.data.id)) {
          return { ...msg, agentTurn: [...agentTurns, { ...event.data, steps: event.data.steps || [] }] };
        }
      }
      return msg;
    });
    this.set('messages', updatedMessages);
    this._handleOnAgentStep(event);
  }

  _handleAgentStepStarted(event) {
    const messages = this.get('messages') || [];
    const updatedMessages = messages.map((msg) => {
      if (msg.id === event.user_message_id) {
        const agentTurns = (msg.agentTurn || []).map((turn) => {
          if (turn.id === event.agent_turn_id) {
            const steps = turn.steps || [];
            // Avoid duplicating the step
            if (!steps.find((step) => step.id === event.data.id)) {
              return { ...turn, steps: [...steps, { ...event.data, toolCalls: event.data.toolCalls || [] }] };
            }
          }
          return turn;
        });
        return { ...msg, agentTurn: agentTurns };
      }
      return msg;
    });
    this.set('messages', updatedMessages);
    this._handleOnAgentStep(event);
  }

  _updateStepState(event, isCompletion = false) {
    const messages = this.get('messages') || [];
    const updatedMessages = messages.map((msg) => {
      if (msg.id === event.user_message_id) {
        const agentTurns = (msg.agentTurn || []).map((turn) => {
          if (turn.id === event.agent_turn_id) {
            const steps = (turn.steps || []).map((step) => {
              if (step.id === (isCompletion ? event.data.id : event.agent_step_id) || step.id === event.data.id) {
                return { ...step, ...event.data };
              }
              return step;
            });
            // If step not found and it's a new step completion, add it (edge case)
            if (isCompletion && !steps.find((s) => s.id === event.data.id)) {
              steps.push({ ...event.data, toolCalls: event.data.toolCalls || [] });
            }
            return { ...turn, steps };
          }
          return turn;
        });
        return { ...msg, agentTurn: agentTurns };
      }
      return msg;
    });
    this.set('messages', updatedMessages);
    this._handleOnAgentStep(event);
  }

  _handleAgentStepUpdated(event) {
    this._updateStepState(event);
  }

  _handleAgentStepCompleted(event) {
    this._updateStepState(event, true);
  }

  _handleToolCallStarted(event) {
    const messages = this.get('messages') || [];
    const updatedMessages = messages.map((msg) => {
      if (msg.id === event.user_message_id) {
        const agentTurns = (msg.agentTurn || []).map((turn) => {
          if (turn.id === event.agent_turn_id) {
            const steps = (turn.steps || []).map((step) => {
              if (step.id === event.agent_step_id) {
                const toolCalls = step.toolCalls || [];
                // Add new tool call, ensuring not to duplicate
                if (
                  !toolCalls.find(
                    (tc) => tc.tool_tracking_id === event.data.tool_tracking_id && tc.tool_name === event.data.tool_name
                  )
                ) {
                  return { ...step, toolCalls: [...toolCalls, event.data] };
                }
              }
              return step;
            });
            return { ...turn, steps };
          }
          return turn;
        });
        return { ...msg, agentTurn: agentTurns };
      }
      return msg;
    });
    this.set('messages', updatedMessages);
  }

  _handleToolCallSucceeded(event) {
    const messages = this.get('messages') || [];
    const updatedMessages = messages.map((msg) => {
      if (msg.id === event.user_message_id) {
        const agentTurns = (msg.agentTurn || []).map((turn) => {
          if (turn.id === event.agent_turn_id) {
            const steps = (turn.steps || []).map((step) => {
              if (step.id === event.agent_step_id) {
                const toolCalls = (step.toolCalls || []).map((tc) => {
                  if (tc.tool_tracking_id === event.data.tool_tracking_id && tc.tool_name === event.data.tool_name) {
                    // Match on tracking_id and name
                    return { ...tc, ...event.data, status: 'SUCCESS' };
                  }
                  return tc;
                });
                return { ...step, toolCalls };
              }
              return step;
            });
            return { ...turn, steps };
          }
          return turn;
        });
        return { ...msg, agentTurn: agentTurns };
      }
      return msg;
    });
    this.set('messages', updatedMessages);
  }

  _handleToolCallFailed(event) {
    const messages = this.get('messages') || [];
    const updatedMessages = messages.map((msg) => {
      if (msg.id === event.user_message_id) {
        const agentTurns = (msg.agentTurn || []).map((turn) => {
          if (turn.id === event.agent_turn_id) {
            const steps = (turn.steps || []).map((step) => {
              if (step.id === event.agent_step_id) {
                const toolCalls = (step.toolCalls || []).map((tc) => {
                  if (tc.tool_tracking_id === event.data.tool_tracking_id && tc.tool_name === event.data.tool_name) {
                    return { ...tc, ...event.data, status: 'FAILED' }; // Ensure status and error are set
                  }
                  return tc;
                });
                return { ...step, toolCalls };
              }
              return step;
            });
            return { ...turn, steps };
          }
          return turn;
        });
        return { ...msg, agentTurn: agentTurns };
      }
      return msg;
    });
    this.set('messages', updatedMessages);
  }

  _handleAgentTurnCompleted(event) {
    const messages = this.get('messages') || [];
    const updatedMessages = messages.map((msg) => {
      if (msg.id === event.user_message_id) {
        const agentTurns = (msg.agentTurn || []).map((turn) => {
          if (turn.id === event.data.id) {
            // event.data.id is the agent_turn_id for this event
            return { ...turn, ...event.data };
          }
          return turn;
        });
        // If the agent turn wasn't found (e.g. if agent_turn_started was missed), add it.
        if (!agentTurns.find((t) => t.id === event.data.id) && msg.agentTurn) {
          agentTurns.push({ ...event.data, steps: event.data.steps || [] });
        } else if (!msg.agentTurn) {
          // if agentTurn array itself doesn't exist
          msg.agentTurn = [{ ...event.data, steps: event.data.steps || [] }];
        }
        return { ...msg, agentTurn: agentTurns };
      }
      return msg;
    });
    this.set('messages', updatedMessages);
  }

  _handleAgentTurnFailed(event) {
    const messages = this.get('messages') || [];
    const updatedMessages = messages.map((msg) => {
      if (msg.id === event.user_message_id) {
        const agentTurns = (msg.agentTurn || []).map((turn) => {
          if (turn.id === event.data.id) {
            // event.data.id is the agent_turn_id for this event
            return { ...turn, ...event.data, status: 'FAILED' };
          }
          return turn;
        });
        // If the agent turn wasn't found (e.g. if agent_turn_started was missed), add it as failed.
        if (!agentTurns.find((t) => t.id === event.data.id) && msg.agentTurn) {
          agentTurns.push({ ...event.data, steps: event.data.steps || [], status: 'FAILED' });
        } else if (!msg.agentTurn) {
          // if agentTurn array itself doesn't exist
          msg.agentTurn = [{ ...event.data, steps: event.data.steps || [], status: 'FAILED' }];
        }
        return { ...msg, agentTurn: agentTurns };
      }
      return msg;
    });
    this.set('messages', updatedMessages);
  }

  subscribe() {
    if (this.id && !this.socket?.id) {
      this.socket = new Socket({
        outType: 'kaizenJoinSession',
        inType: `chatSession:${this.id}`,
        frequency: 60,
        delaySend: true,
        listenToBroadcast: true,
        onSuccess: this.onReceiveEvent,
        onError(err) {
          console.warn('Received Kaizen Subscription Socket Error', err);
        },
        onReconnect: () => {
          this.socket.setOutType('kaizenJoinSession');
          this.socket.send({ session_id: this.id });
        }
      });

      this.socket.send({ session_id: this.id });
    }
  }

  unsubscribe() {
    try {
      this.socket.cancelAll();
      this.socket.destroy();
    } catch (e) {
      // We can ignore this, as error usually means it wasn't open yet
    }
  }

  // Custom method to update session title using the new API endpoint
  async updateSessionTitle(newTitle) {
    return api
      .put(`${this.urlRoot}/${this.id}`, {
        body: { user_title: newTitle }
      })
      .then((response) => {
        this.set('user_title', newTitle);
        this.set('updated_at', new Date().toISOString());
        return response;
      });
  }

  // Override save method to use custom endpoint for updates
  async save(attributes = {}, options = {}) {
    if (this.id && options.patch && (attributes.session_title || attributes.user_title)) {
      // Use the custom update endpoint for title updates
      // Support both session_title and user_title for backwards compatibility
      const newTitle = attributes.user_title || attributes.session_title;
      return this.updateSessionTitle(newTitle);
    }
    // Fall back to default save behavior for new sessions
    return super.save(attributes, options);
  }
}

export default ChatSessionModel;
